import { useEffect, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

/*
 * Push notifications from /v1/events (a per-account Durable Object). Messages
 * only name what changed; queries refetch through the normal endpoints.
 *
 * While connected nothing polls: a change arrives as a message, and reconnecting
 * (after a drop, after the tab was hidden, and every few minutes to re-authenticate)
 * refetches whatever is on screen. When the socket can't connect, polling returns
 * at its fast cadence, so a lost connection only costs latency. A hidden tab
 * disconnects after a minute and holds nothing open.
 */
type Topic = 'approvals' | 'activity' | 'targets' | 'controls' | 'grants' | 'devices' | 'decisions' | 'projects' | 'models' | 'workspaces';
const TOPIC_KEYS: Record<Topic, QueryKey[]> = {
  approvals: [['approvals'], ['overview'], ['project']], activity: [['activity']], targets: [['project'], ['overview']],
  controls: [['target']], grants: [['target'], ['project']], devices: [['device'], ['devices'], ['overview']],
  decisions: [['project']], projects: [['project'], ['projects'], ['overview'], ['policy-defaults']], models: [['models'], ['device']], workspaces: [['workspaces']],
};

let connected = false;
const listeners = new Set<() => void>();
const setConnected = (v: boolean) => { if (connected !== v) { connected = v; listeners.forEach((l) => l()); } };
const subscribe = (l: () => void) => { listeners.add(l); return () => listeners.delete(l); };
export const useLiveConnected = () => useSyncExternalStore(subscribe, () => connected);
/** refetchInterval that polls `fast` only while push is unavailable. */
export const poll = (fast: number) => () => (connected ? false : fast);

export function useLiveUpdates(enabled: boolean) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled || typeof WebSocket === 'undefined') return;
    let socket: WebSocket | null = null; let retry = 0; let stopped = false; let paused = false;
    let reconnect: ReturnType<typeof setTimeout> | undefined; let ping: ReturnType<typeof setInterval> | undefined;
    let renew: ReturnType<typeof setTimeout> | undefined; let hide: ReturnType<typeof setTimeout> | undefined;
    const connect = () => {
      clearTimeout(reconnect);
      const current = socket = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/v1/events`);
      current.onmessage = (event) => {
        if (event.data === 'pong') return;
        let message: { type?: string; topics?: unknown; max_age_ms?: unknown };
        try { message = JSON.parse(String(event.data)); } catch { return; }
        if (message.type === 'connected') {
          retry = 0; setConnected(true);
          // Anything may have changed while we were disconnected.
          void queryClient.invalidateQueries();
          // The server stops sending to a socket older than max_age_ms; reconnect before that, which also re-checks the session.
          clearTimeout(renew);
          renew = setTimeout(() => current.close(1000, 'renew'), (typeof message.max_age_ms === 'number' ? message.max_age_ms : 600_000) * 0.9);
        } else if (message.type === 'invalidate' && Array.isArray(message.topics)) {
          const keys = new Map<string, QueryKey>();
          for (const topic of message.topics) for (const key of TOPIC_KEYS[topic as Topic] ?? []) keys.set(JSON.stringify(key), key);
          for (const key of keys.values()) void queryClient.invalidateQueries({ queryKey: key });
        }
      };
      current.onclose = () => {
        if (socket !== current) return;
        setConnected(false); clearInterval(ping); clearTimeout(renew);
        if (stopped || paused) return;
        // Reconnect promptly after a clean close, otherwise back off with jitter.
        const delay = Math.min(30_000, 1_000 * 2 ** retry++) * (0.5 + Math.random() / 2);
        reconnect = setTimeout(connect, delay);
      };
      clearInterval(ping);
      // Answered by the runtime without waking the Durable Object; it only keeps intermediaries from dropping an idle socket.
      ping = setInterval(() => { if (current.readyState === WebSocket.OPEN) current.send('ping'); }, 25_000);
    };
    // Nobody is looking at a hidden tab: let go of the socket after a minute, and pick up again (refetching) when it is shown.
    const onVisibility = () => {
      clearTimeout(hide);
      if (document.hidden) hide = setTimeout(() => { paused = true; clearTimeout(reconnect); socket?.close(1000, 'hidden'); }, 60_000);
      else if (paused) { paused = false; retry = 0; connect(); }
    };
    document.addEventListener('visibilitychange', onVisibility);
    connect();
    return () => {
      stopped = true; document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(reconnect); clearTimeout(renew); clearTimeout(hide); clearInterval(ping); socket?.close(1000, 'leaving'); setConnected(false);
    };
  }, [enabled, queryClient]);
}
